<script setup lang="ts">
import { ref } from 'vue'
import ImageRemote from '@/mixit-ui/components/media/ImageRemote.vue'
import Tag from '../../mixit-ui/components/misc/Tag.vue'
import ButtonQuantity from '../../mixit-ui/components/button/ButtonQuantity.vue'
import type { CartProduct } from '@/types/cart'
import type { ProductImageData } from '@/types-new/product'
import { cartService } from '@/services-new/cartService'
import { overlayManager } from '@/mixit-ui/overlayService'

const props = defineProps<{ item: CartProduct }>()

const firstImageData = (props.item.images[0] as { data: ProductImageData } | undefined)?.data

const isDeleting = ref(false)

async function handleDelete() {
  const confirmed = await overlayManager.confirm({
    icon: 'mi-bin',
    title: 'Odebrat produkt',
    message: `Opravdu chcete odebrat "${props.item.name}" z košíku?`,
    confirmSeverity: 'danger',
    confirmText: 'Odebrat',
    cancelText: 'Zrušit',
  })
  if (!confirmed) return

  isDeleting.value = true
  try {
    await cartService.deleteProduct(props.item.id)
    overlayManager.showToast({
      message: `Produkt ${props.item.name} byl odebrán z košíku.`,
      severity: 'success',
      icon: 'mi-bin',
    })
  } finally {
    isDeleting.value = false
  }
}
</script>

<template>
  <div class="flex flex-col gap-4 w-full bg-white rounded-sm">
    <div class="relative flex flex-row gap-4 w-full items-start">
      <RouterLink
        :to="{ name: 'productPath', params: { permalink: item.permalink } }"
        class="absolute inset-0 z-2"
      />
      <div class="w-17.5 shrink-0">
        <ImageRemote
          v-if="firstImageData"
          :data="firstImageData"
          img-class="w-full"
        />
      </div>
      <div class="flex flex-col gap-5 justify-between flex-1">
        <div class="flex flex-col gap-2 items-start flex-1">
          <div class="flex flex-col gap-1">
            <span class="font-roboto-slab text-xs lg:text-sm text-brown-600 font-bold">{{ item.name }}</span>
            <span class="font-roboto-slab text-2xs lg:text-xs text-brown-300 font-bold">{{ item.weight }}&nbsp;{{ item.unit }}</span>
          </div>
          <span
            v-if="false"
            class="flex flex-col gap-1"
          >
            <Tag
              label=""
              severity="danger"
              size="sm"
            />
          </span>
        </div>
        <div class="flex flex-row gap-6 justify-between">
          <div class="hidden lg:flex z-3 relative">
            <ButtonQuantity
              init-label="toCart"
              size="xs"
              :product-id="item.productID"
            />
          </div>
          <div class="flex flex-row gap-2 items-center justify-end">
            <span class="hidden lg:flex flex-row gap-1 items-center text-2xs">
              <template v-if="false">
                <!-- discount: no original price field in CartProduct -->
                <span class="font-roboto-slab text-red-500 text-xs font-bold">{{ item.price }}</span>
                <span class="font-roboto-slab text-brown-600 font-bold line-through">{{ item.price }}</span>
              </template>
              <template v-else>
                <span class="font-roboto-slab text-brown-600 font-bold">{{ item.price }}</span>
              </template>
              <strong class="text-brown-300 text-2xs">/ks</strong>
            </span>
            <span class="flex font-roboto-slab text-brown-600 text-xs lg:text-sm font-bold">{{ item.totalPrice }}</span>
            <button
              type="button"
              class="relative z-3 cursor-pointer active:scale-90 transition text-red-500"
              :disabled="isDeleting"
              @click.stop="handleDelete"
            >
              <i
                class="mi-bin text-2xl transition"
                :class="{ 'opacity-50': isDeleting }"
              />
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
